// EPIC I PR1 — Service facturation
// Pattern projet : INSERT/transition status via RPC SECURITY DEFINER, SELECT/UPDATE direct.
import { supabase } from '../../lib/supabaseClient';
import type { Tables, Json } from '../../types/database.types';
import type { LineNature } from '../../lib/billing/lineTotals';

export type BillingDocument = Tables<'billing_documents'>;
export type BillingLineItem = Tables<'billing_line_items'>;
export type BillingDocumentType = BillingDocument['doc_type'];
export type BillingDocumentStatus = BillingDocument['status'];

export const BILLING_DOC_TYPE_LABELS: Record<BillingDocumentType, string> = {
  invoice: 'Facture',
  estimate: 'Devis',
  credit_note: 'Avoir',
};

export const BILLING_STATUS_LABELS: Record<BillingDocumentStatus, string> = {
  draft: 'Brouillon',
  sent: 'Envoyée',
  paid: 'Payée',
  overdue: 'En retard',
  void: 'Annulée',
  accepted: 'Acceptée',
  rejected: 'Refusée',
};

export interface BillingFilters {
  doc_type?: BillingDocumentType;
  status?: BillingDocumentStatus[];
  dossier_id?: string;
  lawyer_id?: string;
  client_contact_id?: string;
  from?: string;
  to?: string;
}

// ─── SELECT ──────────────────────────────────────────────────────────────

export async function listBillingDocuments(filters: BillingFilters = {}): Promise<BillingDocument[]> {
  let q = supabase.from('billing_documents').select('*');
  if (filters.doc_type) q = q.eq('doc_type', filters.doc_type);
  if (filters.status?.length) q = q.in('status', filters.status);
  if (filters.dossier_id) q = q.eq('dossier_id', filters.dossier_id);
  if (filters.lawyer_id) q = q.eq('lawyer_id', filters.lawyer_id);
  if (filters.client_contact_id) q = q.eq('client_contact_id', filters.client_contact_id);
  if (filters.from) q = q.gte('created_at', filters.from);
  if (filters.to) q = q.lte('created_at', filters.to);
  q = q.order('created_at', { ascending: false });
  const { data, error } = await q;
  if (error) throw new Error(`Failed to list billing documents: ${error.message}`);
  return data ?? [];
}

export async function getBillingDocument(id: string): Promise<BillingDocument | null> {
  const { data, error } = await supabase
    .from('billing_documents')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(`Failed to fetch billing document: ${error.message}`);
  return data;
}

export async function listLineItems(documentId: string): Promise<BillingLineItem[]> {
  const { data, error } = await supabase
    .from('billing_line_items')
    .select('*')
    .eq('billing_document_id', documentId)
    .order('sort_order', { ascending: true });
  if (error) throw new Error(`Failed to list line items: ${error.message}`);
  return data ?? [];
}

/** Nature et HT de chaque ligne des pièces demandées (composition dans la liste). */
export async function listLineNatures(
  documentIds: string[],
): Promise<Pick<BillingLineItem, 'billing_document_id' | 'nature' | 'ht_total'>[]> {
  if (documentIds.length === 0) return [];
  const { data, error } = await supabase
    .from('billing_line_items')
    .select('billing_document_id, nature, ht_total')
    .in('billing_document_id', documentIds);
  if (error) throw new Error(`Failed to list line natures: ${error.message}`);
  return data ?? [];
}

export type BillingPayment = Tables<'billing_payments'>;

export interface BillingPaymentRow extends BillingPayment {
  document: Pick<
    BillingDocument,
    'id' | 'number' | 'notes' | 'status' | 'ttc_total' | 'remaining_amount' | 'dossier_id' | 'issue_date' | 'doc_type'
  > & {
    client: { first_name: string | null; last_name: string | null; company_name: string | null } | null;
  };
}

/** Règlements copiés de Polaris, du plus récent au plus ancien (RLS : pièces visibles). */
export async function listBillingPayments(): Promise<BillingPaymentRow[]> {
  const { data, error } = await supabase
    .from('billing_payments')
    .select(
      '*, document:billing_documents!inner(id, number, notes, status, ttc_total, remaining_amount, dossier_id, issue_date, doc_type, client:contacts!billing_documents_client_contact_id_fkey(first_name, last_name, company_name))',
    )
    .order('paid_on', { ascending: false })
    .order('synced_at', { ascending: false });
  if (error) throw new Error(`Failed to list payments: ${error.message}`);
  return (data ?? []) as unknown as BillingPaymentRow[];
}

// ─── RÉSUMÉ FINANCIER ─────────────────────────────────────────────────────

export interface FinancialSummary {
  invoicedTtc: number;   // total TTC des factures émises (hors brouillons/annulées)
  paidTtc: number;       // total TTC encaissé (factures payées)
  outstandingTtc: number; // reste dû (émis non payé, hors annulées)
  overdueTtc: number;    // part du reste dû en retard
  invoiceCount: number;
  creditNoteTtc: number; // total des avoirs émis
}

/**
 * Calcule le résumé financier d'un jeu de documents de facturation
 * (typiquement filtrés par client_contact_id ou dossier_id).
 * Fonction pure : les brouillons et documents annulés (void) sont exclus
 * des totaux émis ; les devis ne comptent pas comme facturation.
 */
export function computeFinancialSummary(docs: BillingDocument[]): FinancialSummary {
  const summary: FinancialSummary = {
    invoicedTtc: 0,
    paidTtc: 0,
    outstandingTtc: 0,
    overdueTtc: 0,
    invoiceCount: 0,
    creditNoteTtc: 0,
  };

  for (const d of docs) {
    if (d.status === 'draft' || d.status === 'void') continue;

    if (d.doc_type === 'credit_note') {
      summary.creditNoteTtc += d.ttc_total;
      continue;
    }
    if (d.doc_type !== 'invoice') continue; // ignore les devis

    summary.invoiceCount += 1;
    summary.invoicedTtc += d.ttc_total;

    if (d.status === 'paid') {
      summary.paidTtc += d.ttc_total;
    } else {
      summary.outstandingTtc += d.ttc_total;
      if (d.status === 'overdue') summary.overdueTtc += d.ttc_total;
    }
  }

  return summary;
}

// ─── MUTATIONS ──────────────────────────────────────────────────────────

export interface GenerateInvoiceInput {
  dossier_id: string;
  time_entry_ids: string[];
  client_contact_id?: string | null;
  vat_rate?: number;
  due_date?: string | null;
}

export async function generateInvoiceFromTimeEntries(
  input: GenerateInvoiceInput,
): Promise<BillingDocument> {
  const { data, error } = await supabase.rpc('generate_invoice_from_time_entries', {
    p_dossier_id: input.dossier_id,
    p_time_entry_ids: input.time_entry_ids,
    p_client_contact_id: input.client_contact_id ?? undefined,
    p_vat_rate: input.vat_rate ?? 20,
    p_due_date: input.due_date ?? undefined,
  });
  if (error) throw new Error(`Failed to generate invoice: ${error.message}`);
  return data as BillingDocument;
}

export interface CreateBillingDocumentInput {
  dossier_id: string;
  doc_type?: BillingDocumentType;
  client_contact_id?: string | null;
  vat_rate?: number;
  due_date?: string | null;
  notes?: string | null;
}

export async function createBillingDocument(
  input: CreateBillingDocumentInput,
): Promise<BillingDocument> {
  const { data, error } = await supabase.rpc('create_billing_document', {
    p_dossier_id: input.dossier_id,
    p_doc_type: input.doc_type ?? 'invoice',
    p_client_contact_id: input.client_contact_id ?? undefined,
    p_vat_rate: input.vat_rate ?? 20,
    p_due_date: input.due_date ?? undefined,
    p_notes: input.notes ?? undefined,
  });
  if (error) throw new Error(`Failed to create billing document: ${error.message}`);
  return data as BillingDocument;
}

export async function issueBillingDocument(id: string): Promise<BillingDocument> {
  const { data, error } = await supabase.rpc('issue_billing_document', { p_id: id });
  if (error) throw new Error(`Failed to issue billing document: ${error.message}`);
  return data as BillingDocument;
}

export async function markBillingPaid(
  id: string,
  paid_at?: string | null,
  payment_method?: string | null,
): Promise<BillingDocument> {
  const { data, error } = await supabase.rpc('mark_billing_paid', {
    p_id: id,
    p_paid_at: paid_at ?? undefined,
    p_payment_method: payment_method ?? undefined,
  });
  if (error) throw new Error(`Failed to mark billing paid: ${error.message}`);
  return data as BillingDocument;
}

export async function voidBillingDocument(id: string): Promise<BillingDocument> {
  const { data, error } = await supabase.rpc('void_billing_document', { p_id: id });
  if (error) throw new Error(`Failed to void billing document: ${error.message}`);
  return data as BillingDocument;
}

export async function deleteBillingDocument(id: string): Promise<void> {
  const { error } = await supabase.from('billing_documents').delete().eq('id', id);
  if (error) throw new Error(`Failed to delete billing document: ${error.message}`);
}

// ─── I-05 — éditeur lignes ──────────────────────────────────────────────

export type BillingLineUnit = 'hour' | 'item' | 'forfait';

export interface BillingLineDraft {
  /** Conservé si la ligne provient de la facturation auto depuis time_entries. */
  time_entry_id?: string | null;
  description: string;
  quantity: number;
  unit: BillingLineUnit;
  unit_price: number;
  sort_order: number;
  /** Nature de la ligne ; absente = honoraires. */
  nature?: LineNature;
  /** Taux de la ligne ; absent ou null = taux du document. */
  vat_rate?: number | null;
}

/**
 * Remplace toutes les lignes d'un brouillon. Les lignes liées à des time_entries
 * non présentes dans la nouvelle liste seront libérées (invoiced_in_id = null).
 * Renvoie les lignes telles que stockées (avec id, ht_total recalculé).
 */
export async function replaceBillingLines(
  documentId: string,
  lines: BillingLineDraft[],
): Promise<BillingLineItem[]> {
  const { data, error } = await supabase.rpc('replace_billing_lines', {
    p_doc_id: documentId,
    p_lines: lines as unknown as Json,
  });
  if (error) throw new Error(`Failed to replace billing lines: ${error.message}`);
  return (data ?? []) as BillingLineItem[];
}

export interface UpdateBillingMetaInput {
  notes?: string | null;
  due_date?: string | null;
  vat_rate?: number | null;
}

export async function updateBillingDocumentMeta(
  id: string,
  input: UpdateBillingMetaInput,
): Promise<BillingDocument> {
  const { data, error } = await supabase.rpc('update_billing_document_meta', {
    p_doc_id: id,
    p_notes: input.notes ?? undefined,
    p_due_date: input.due_date ?? undefined,
    p_vat_rate: input.vat_rate ?? undefined,
  });
  if (error) throw new Error(`Failed to update billing meta: ${error.message}`);
  return data as BillingDocument;
}

// ─── I-09 — Devis ───────────────────────────────────────────────────────

export async function generateEstimateFromTimeEntries(
  input: GenerateInvoiceInput,
): Promise<BillingDocument> {
  const { data, error } = await supabase.rpc('generate_estimate_from_time_entries', {
    p_dossier_id: input.dossier_id,
    p_time_entry_ids: input.time_entry_ids,
    p_client_contact_id: input.client_contact_id ?? undefined,
    p_vat_rate: input.vat_rate ?? 20,
    p_due_date: input.due_date ?? undefined,
  });
  if (error) throw new Error(`Failed to generate estimate: ${error.message}`);
  return data as BillingDocument;
}

export async function convertEstimateToInvoice(
  estimateId: string,
  dueDate?: string | null,
): Promise<BillingDocument> {
  const { data, error } = await supabase.rpc('convert_estimate_to_invoice', {
    p_estimate_id: estimateId,
    p_due_date: dueDate ?? undefined,
  });
  if (error) throw new Error(`Failed to convert estimate: ${error.message}`);
  return data as BillingDocument;
}

// ─── HELPERS ────────────────────────────────────────────────────────────

export function formatEuros(amount: number | string | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
}
